import * as React from 'react';
import { FunctionComponent } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import ProximityLocation from 'application/Models/ProximityLocation';
import { formatDollar, formatTruncatedDollar } from 'helpers/formatters';
import { ROUTES } from '../../../../../constants';
import { Spacing } from '../../../../Layout';

export interface AggDetailsProximityLocation extends ProximityLocation {
    hasFailedAggCheck: boolean;
    aggCheckOverage: number;
}

interface AggregationDetailsProps {
    proximityLocations: AggDetailsProximityLocation[];
    distance: number;
    referenceTIV: number;
}

const AggregationDetail: FunctionComponent<AggregationDetailsProps> = (props) => {
    const { proximityLocations, distance, referenceTIV } = props;
    return (
        <Card style={{ userSelect: 'text' }}>
            <CardHeader
                title={`Proximity Locations (${distance} miles)`}
                subheader={
                    <i>
                        denotes listed location's AGG limit would be exceeded if new location is bound (Amount exceeding
                        Agg limit)
                    </i>
                }
                subheaderTypographyProps={{ variant: 'subtitle1', sx: { color: 'red' } }}
            />
            <CardContent>
                <Table size={'small'}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Location Name</TableCell>
                            <TableCell>Exp. Date</TableCell>
                            <TableCell>TIV</TableCell>
                            <TableCell>WHH Ded.</TableCell>
                            <TableCell>ITV</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {proximityLocations.map((pl) => (
                            <TableRow>
                                <TableCell>
                                    <Link
                                        target={'_blank'}
                                        href={ROUTES.location(String(pl.submissionId), pl.id)}
                                        underline="hover"
                                    >
                                        <span style={pl.hasFailedAggCheck ? { color: 'red' } : {}}>{pl.name}</span>
                                    </Link>
                                    {pl.hasFailedAggCheck && (
                                        <Typography
                                            style={{ display: 'inline-block', color: 'red' }}
                                            variant={'subtitle2'}
                                        >
                                            <i>&nbsp;{`(${formatTruncatedDollar(pl.aggCheckOverage, true)})`}</i>
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>{pl.expirationDate}</TableCell>
                                <TableCell align={'right'}>
                                    {pl.primaryLimit !== 'Full' ? (
                                        <Tooltip title={pl.primaryLimit} PopperProps={{ style: { zIndex: 3000 } }}>
                                            <span>{'(p*) '}</span>
                                        </Tooltip>
                                    ) : (
                                        ''
                                    )}
                                    <span>{`${formatDollar(pl.totalInsurableValue, 0)}`}</span>
                                </TableCell>
                                <TableCell>{pl.whhDeductible}</TableCell>
                                <TableCell>{formatDollar(pl.insuranceToValue, 0)}</TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell>Totals</TableCell>
                            <TableCell />
                            <TableCell align="right">
                                {formatDollar(
                                    proximityLocations.reduce((prev, cur) => prev + cur.totalInsurableValue, 0),
                                    0
                                )}
                            </TableCell>
                            <TableCell colSpan={2} align="right">
                                <b>{`Agg TIV ${formatDollar(
                                    referenceTIV +
                                        proximityLocations.reduce((prev, cur) => prev + cur.totalInsurableValue, 0),
                                    0
                                )}`}</b>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                <Spacing height={'16px'} />
                <Typography align={'right'} variant={'subtitle2'}>
                    (p*) denotes location has a primary limit
                </Typography>
            </CardContent>
        </Card>
    );
};

export default AggregationDetail;
